/**
 * The room-wide vote's bounds and its one piece of arithmetic.
 *
 * A vote is the room asking everybody in it a question with a fixed set of answers, waiting
 * VOTE_DURATION_SECONDS, and then moving mass from the people who answered with the minority to
 * the people who answered with the majority. It is a **general mechanism**: nothing here knows
 * what the question is or who asked it, and `RoomSim.openVote` is the one door in.
 *
 * **NOTHING FREEZES, AND THAT IS THE SINGLE MOST IMPORTANT DECISION IN THE FEATURE.** The match
 * runs normally for every player for the whole ten seconds — freezing a hundred players because
 * one of them touched something would rescue everybody who was about to be eaten and rob
 * everybody who was about to eat, which is a far larger change to the game than the vote is. No
 * phase of `step` gates on a vote being open except the vote's own, and `room-sim.test.ts`
 * asserts it rather than describing it.
 *
 * Every number here is world state's bound rather than a display decision: the duration decides
 * the tick a vote resolves on, the loss share decides how much mass moves, and the two lengths
 * are what the wire will carry. The client's half of this feature is its own card.
 */

import { STARTING_MASS, TICK_RATE_HZ } from './simulation.js';

/**
 * How long a vote stays open. Ten seconds, which is DUEL_DECIDE_TICKS' own window and for its
 * reason: it is the longest a player will read a card over a moving arena before it stops being
 * a decision and starts being an interruption.
 *
 * A player who says nothing in that time is a silent opt-out with no consequence — an abstainer
 * is in neither side, pays nothing and is paid nothing — so the deadline is a deadline for
 * *answering* rather than a deadline anybody has to beat.
 */
export const VOTE_DURATION_SECONDS = 10;

/** The same window in ticks, which is what `VoteState.endsAtTick` is measured in. */
export const VOTE_DURATION_TICKS = VOTE_DURATION_SECONDS * TICK_RATE_HZ;

/**
 * What one loser pays: half of the mass they were carrying **at the tick their answer was
 * recorded**, never at resolution.
 *
 * The stake is snapshotted at vote time because the alternative is a ten-second incentive to grow
 * or to shrink: a stake read at resolution would mean the number on the card is not the number
 * that applies, and the ten seconds a player should spend deciding would be ten seconds spent
 * gaming the settlement instead.
 *
 * Half rather than a quarter (DUEL_MASS_TRANSFER's share) because a vote is a decision somebody
 * made rather than a collision they walked into, and because being on the wrong side of one is
 * meant to be worth avoiding. It cannot kill: what is actually taken is floored at
 * VOTE_LOSS_FLOOR_MASS, and what the winners share is what the floor allowed rather than what
 * was asked for — see `resolveVote`.
 */
export const VOTE_LOSS_SHARE = 0.5;

/**
 * The mass no vote may take a player below. STARTING_MASS — the size everybody spawns at.
 *
 * **A VOTE CAN NEVER KILL YOU**, which is what makes answering one a real choice rather than a
 * gamble a small player must decline. A player at or under this floor pays nothing at all and is
 * still counted in the tally: their answer decided the majority even though it moved no mass,
 * which is the honest reading of "the pot is what was actually taken".
 *
 * Named rather than STARTING_MASS at the call site because what this is *about* is a floor on a
 * settlement, and a later card that wanted a different floor should not have to argue with the
 * spawn size to get one.
 */
export const VOTE_LOSS_FLOOR_MASS = STARTING_MASS;

/**
 * A vote must offer at least two answers, because one answer is not a question, and at most four,
 * because that is what fits on a card a player reads while steering.
 *
 * The upper bound is also the wire's: `serverVoteSchema` refuses a longer list, so a forged frame
 * cannot make a client lay out a ballot the size of the arena.
 */
export const MIN_VOTE_OPTIONS = 2;
export const MAX_VOTE_OPTIONS = 4;

/**
 * The longest question and the longest answer label the wire will carry.
 *
 * Both are bounds rather than budgets: a vote frame is built once per room and encoded once (see
 * `serverVoteSchema`), so what a prompt costs is a few hundred bytes twice in a vote's life. They
 * exist so that a frame cannot carry a megabyte of "question" to every socket in a 200-player
 * room, which is the same reason `sayingSnapshotSchema` bounds its text.
 */
export const MAX_VOTE_PROMPT_LENGTH = 120;
export const MAX_VOTE_OPTION_LABEL_LENGTH = 48;

/**
 * The longest option id a client may send back as its answer.
 *
 * An **id**, never a label, and never an index: `miniGameChoiceSchema`'s rule one feature along.
 * The simulation checks the value against the running vote's own option list, so a client cannot
 * answer a vote with something that is not one of its answers — and it cannot answer by position
 * either, which would make a reordered ballot a different vote.
 */
export const MAX_VOTE_OPTION_ID_LENGTH = 32;
